import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SectionHeader, SpecraBadge, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraProgress } from '@mono/ui-components';
import { Activity, AlertCircle, CheckCircle2, ChevronRight, Clock, Sparkles, Target } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../../contexts/authContext';
import { useComplianceMatrix } from '../../../../hooks/useComplianceMatrix';
import { useProjectActivity } from '../../../../hooks/useProjectActivity';
import { useProjectVolumes } from '../../../../hooks/useProjectVolumes';
import { useProposalProject } from '../../../../hooks/useProposalProject';
import { useSolicitationInsights } from '../../../../hooks/useSolicitationInsights';
import { useLayoutStore } from '../../../../lib/stores/LayoutStore';
import { ComplianceStatus } from '../../../../types/proposal';
import { deadlineCountdown, greeting } from '../../../../utils/time';
import { AssignedSectionWork } from '../home/AssignedSectionWork';
import { MilestonesPanel } from '../home/MilestonesPanel';
import { ResponseStructurePanel } from '../volume/ResponseStructurePanel';
import { proposalLoadErrorMessage } from './proposalLoadError';
/**
 * Redesigned proposal home page (behind `specra_redesign_proposal_home` flag).
 *
 * Layout: full-width main content area + fixed-width right rail.
 * Main content: greeting, assigned requirements, compliance snapshot, next actions.
 * Right rail: project metadata, deadline countdown, recent activity.
 *
 * Uses the same hooks and query keys as the existing HomePage so React Query
 * deduplicates requests when the user navigates between old and new.
 */ export function HomePageRedesign() {
    const { id } = useParams();
    const { user } = useAuth();
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    const { data, isLoading, error } = useProposalProject(id ?? '');
    const matrix = useComplianceMatrix(id ?? '');
    const activity = useProjectActivity(id, {
        pageSize: 5
    });
    // These hooks are called unconditionally so the cache stays warm for
    // other V2 pages that also use them (React Query dedup).
    const insights = useSolicitationInsights(id ?? '');
    useProjectVolumes(id);
    if (!id) return null;
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center text-sm text-muted-foreground",
            children: "Loading project..."
        });
    }
    if (error || !data) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center text-sm text-danger",
            children: proposalLoadErrorMessage(error)
        });
    }
    const sol = data.solicitation;
    const stats = matrix.data?.statistics;
    const addressedPct = stats && stats.total > 0 ? Math.round(stats.addressed / stats.total * 100) : 0;
    const myRequirements = (matrix.data?.requirements ?? []).filter((r)=>r.assignedToUserId && user && r.assignedToUserId === user.id).slice(0, 6);
    const dueDate = sol?.dueDate ? new Date(sol.dueDate) : null;
    const dueValid = dueDate && !Number.isNaN(dueDate.getTime());
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 overflow-auto p-6",
                children: [
                    /*#__PURE__*/ _jsxs("header", {
                        className: "mb-6",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-2xl font-semibold tracking-[-0.01em] text-foreground",
                                children: greeting(user?.name)
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: "mt-1 text-sm text-muted-foreground",
                                children: [
                                    "Here is what needs your attention on",
                                    ' ',
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium text-foreground",
                                        children: data.project.name
                                    }),
                                    "."
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ResponseStructurePanel, {
                        projectId: id
                    }),
                    /*#__PURE__*/ _jsx(AssignedSectionWork, {
                        projectId: id
                    }, id),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "mb-6",
                        children: [
                            /*#__PURE__*/ _jsx(SectionHeader, {
                                title: "Your requirement assignments",
                                action: myRequirements.length > 0 ? /*#__PURE__*/ _jsx(Link, {
                                    to: `/proposal/${id}/v2/compliance`,
                                    className: "text-xs font-medium text-brand hover:underline",
                                    children: "View all"
                                }) : undefined
                            }),
                            matrix.isLoading ? /*#__PURE__*/ _jsx(SkeletonRows, {
                                count: 3
                            }) : matrix.isError ? /*#__PURE__*/ _jsx(SpecraCard, {
                                children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                                    children: /*#__PURE__*/ _jsx(WidgetError, {
                                        message: "Failed to load your requirements.",
                                        onRetry: ()=>matrix.refetch()
                                    })
                                })
                            }) : myRequirements.length === 0 ? /*#__PURE__*/ _jsx(SpecraCard, {
                                children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    className: "flex items-center gap-3 text-sm text-muted-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx(CheckCircle2, {
                                            className: "h-5 w-5 text-success",
                                            "aria-hidden": "true"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "font-medium text-foreground",
                                                    children: "Nothing on your plate"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-xs",
                                                    children: "Once teammates assign requirements to you, they will show up here."
                                                })
                                            ]
                                        })
                                    ]
                                })
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "space-y-2",
                                children: myRequirements.map((req)=>/*#__PURE__*/ _jsx(RequirementRow, {
                                        req: req,
                                        projectId: id
                                    }, req.requirementId))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "mb-6",
                        children: [
                            /*#__PURE__*/ _jsx(SectionHeader, {
                                title: "Compliance snapshot"
                            }),
                            matrix.isLoading ? /*#__PURE__*/ _jsx(SkeletonRows, {
                                count: 2
                            }) : matrix.isError ? /*#__PURE__*/ _jsx(SpecraCard, {
                                children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                                    children: /*#__PURE__*/ _jsx(WidgetError, {
                                        message: "Failed to load the compliance matrix.",
                                        onRetry: ()=>matrix.refetch()
                                    })
                                })
                            }) : !stats ? /*#__PURE__*/ _jsx(SpecraCard, {
                                children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    className: "flex items-center gap-3 text-sm text-muted-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx(Target, {
                                            className: "h-5 w-5",
                                            "aria-hidden": "true"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "Process the solicitation to populate the compliance matrix."
                                        })
                                    ]
                                })
                            }) : /*#__PURE__*/ _jsx(SpecraCard, {
                                children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mb-3 flex items-baseline justify-between text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-muted-foreground",
                                                    children: [
                                                        stats.addressed,
                                                        " of ",
                                                        stats.total,
                                                        " requirements addressed"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "font-semibold text-foreground",
                                                    children: [
                                                        addressedPct,
                                                        "%"
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraProgress, {
                                            "aria-label": "Compliance requirements addressed",
                                            value: addressedPct,
                                            tone: addressedPct >= 80 ? 'success' : addressedPct >= 40 ? 'brand' : 'warning'
                                        }),
                                        /*#__PURE__*/ _jsx(ComplianceBreakdown, {
                                            stats: stats
                                        }),
                                        /*#__PURE__*/ _jsxs(Link, {
                                            to: `/proposal/${id}/v2/compliance`,
                                            className: "mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline",
                                            children: [
                                                "Open compliance matrix",
                                                /*#__PURE__*/ _jsx(ChevronRight, {
                                                    className: "h-3 w-3"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        children: [
                            /*#__PURE__*/ _jsx(SectionHeader, {
                                title: "Next best actions"
                            }),
                            /*#__PURE__*/ _jsx(SpecraCard, {
                                children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    className: "flex flex-col items-center gap-2 py-8 text-center",
                                    children: [
                                        /*#__PURE__*/ _jsx(Sparkles, {
                                            className: "h-6 w-6 text-brand",
                                            "aria-hidden": "true"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-sm font-medium text-foreground",
                                            children: "AI-powered suggestions"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "max-w-xs text-xs text-muted-foreground",
                                            children: "Personalized next actions will appear here based on your role and proposal progress."
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("aside", {
                className: "hidden w-80 flex-shrink-0 overflow-auto border-l border-border lg:block",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-4 p-5",
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraCardHeader, {
                                    children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                        children: "Project info"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraCardContent, {
                                    children: sol ? /*#__PURE__*/ _jsx(MetadataKeyValueList, {
                                        sol: sol
                                    }) : /*#__PURE__*/ _jsx("span", {
                                        className: "text-xs text-muted-foreground",
                                        children: "No solicitation data available."
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Clock, {
                                            className: "h-4 w-4 text-muted-foreground"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                            children: "Deadline"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                    children: [
                                        dueValid ? /*#__PURE__*/ _jsx(DeadlineBlock, {
                                            dueDate: dueDate
                                        }) : /*#__PURE__*/ _jsx("div", {
                                            className: "text-sm text-muted-foreground",
                                            children: "No deadline set"
                                        }),
                                        /*#__PURE__*/ _jsx(DeadlineSubRows, {
                                            insights: insights.data
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraCard, {
                            children: /*#__PURE__*/ _jsx(SpecraCardContent, {
                                children: /*#__PURE__*/ _jsx(MilestonesPanel, {
                                    projectId: id
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs(SpecraCard, {
                            children: [
                                /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Activity, {
                                            className: "h-4 w-4 text-muted-foreground"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraCardTitle, {
                                            children: "Recent activity"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraCardContent, {
                                    children: activity.isLoading ? /*#__PURE__*/ _jsx(SkeletonRows, {
                                        count: 3
                                    }) : activity.isError ? /*#__PURE__*/ _jsx(WidgetError, {
                                        message: "Failed to load recent activity.",
                                        onRetry: ()=>activity.refetch()
                                    }) : (activity.data?.entries.length ?? 0) === 0 ? /*#__PURE__*/ _jsx("div", {
                                        className: "text-xs text-muted-foreground",
                                        children: "No activity yet. The first team change will appear here."
                                    }) : /*#__PURE__*/ _jsx("div", {
                                        className: "space-y-2.5",
                                        children: (activity.data?.entries ?? []).slice(0, 5).map((e)=>/*#__PURE__*/ _jsxs("div", {
                                                style: {
                                                    display: 'grid',
                                                    gridTemplateColumns: '52px 8px 1fr',
                                                    gap: '8px'
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "font-mono text-[10px] text-muted-foreground pt-0.5",
                                                        children: e.at?.toDate().toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric'
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: `size-1.5 rounded-full mt-1.5 ${activityCategoryTone(e.category)}`
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "text-xs text-foreground/80 leading-relaxed",
                                                        children: e.summary
                                                    })
                                                ]
                                            }, e.id))
                                    })
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function DeadlineBlock({ dueDate }) {
    const { days, hours, overdue } = deadlineCountdown(dueDate);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            overdue ? /*#__PURE__*/ _jsx("div", {
                className: "text-2xl font-bold tabular-nums text-danger",
                children: "Overdue"
            }) : /*#__PURE__*/ _jsxs("div", {
                className: "flex items-baseline gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-3xl font-bold tabular-nums text-foreground",
                                children: days
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "ml-1 text-xs text-muted-foreground",
                                children: "days"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-3xl font-bold tabular-nums text-foreground",
                                children: hours
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "ml-1 text-xs text-muted-foreground",
                                children: "hrs"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-1.5 text-xs text-muted-foreground",
                children: dueDate.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                })
            })
        ]
    });
}
function RequirementRow({ req, projectId }) {
    return /*#__PURE__*/ _jsxs(Link, {
        to: `/proposal/${projectId}/v2/compliance`,
        className: "group flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm transition-colors hover:border-brand/30 hover:bg-accent",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "line-clamp-2 text-foreground",
                        children: req.source?.exactText || 'Untitled requirement'
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-1 flex items-center gap-2 text-[11px] text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx(StatusPill, {
                                status: req.status
                            }),
                            req.source?.filename && /*#__PURE__*/ _jsx("span", {
                                className: "truncate",
                                children: req.source.filename
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ChevronRight, {
                className: "h-4 w-4 flex-shrink-0 text-muted-foreground/50 group-hover:text-brand",
                "aria-hidden": "true"
            })
        ]
    });
}
function StatusPill({ status }) {
    const variantMap = {
        [ComplianceStatus.NOT_STARTED]: 'neutral',
        [ComplianceStatus.IN_PROGRESS]: 'info',
        [ComplianceStatus.ADDRESSED]: 'success',
        [ComplianceStatus.PARTIAL]: 'warning',
        [ComplianceStatus.NON_COMPLIANT]: 'danger',
        [ComplianceStatus.NOT_APPLICABLE]: 'neutral'
    };
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: variantMap[status] ?? 'neutral',
        children: humanizeStatus(status)
    });
}
/** Inline error affordance for secondary widgets — distinguishes a failed
 * fetch from a genuinely-empty result. */ function WidgetError({ message, onRetry }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-3 text-sm text-danger",
        children: [
            /*#__PURE__*/ _jsx(AlertCircle, {
                className: "h-5 w-5 flex-shrink-0",
                "aria-hidden": "true"
            }),
            /*#__PURE__*/ _jsx("span", {
                children: message
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: onRetry,
                className: "ml-auto flex-shrink-0 text-xs font-medium text-brand hover:underline",
                children: "Retry"
            })
        ]
    });
}
function SkeletonRows({ count }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-2",
        children: Array.from({
            length: count
        }).map((_, i)=>/*#__PURE__*/ _jsx("div", {
                className: "h-12 animate-pulse rounded-lg bg-border"
            }, i))
    });
}
/** Fix 4 — Deadline sub-rows sourced from solicitation insights. */ function DeadlineSubRows({ insights }) {
    // Prefer pipeline deadlines, fall back to legacy array.
    const deadlines = insights?.pipeline?.deadlines ?? insights?.deadlines ?? [];
    if (deadlines.length === 0) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "mt-3 space-y-1.5 border-t border-border pt-3",
        children: deadlines.map((d, i)=>{
            const typeLabel = d.type?.replace(/_/g, ' ') ?? 'Deadline';
            const tone = deadlineTypeTone(d.type);
            return /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 text-xs",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: `size-2 flex-shrink-0 rounded-full ${tone}`
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground capitalize",
                        children: typeLabel
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "ml-auto font-mono text-muted-foreground",
                        children: d.date
                    })
                ]
            }, `${d.type}-${i}`);
        })
    });
}
/** Fix 6 — Key-value metadata rows replacing the badge cluster. */ function MetadataKeyValueList({ sol }) {
    const rows = [
        sol.agency ? {
            label: 'Agency',
            value: sol.agency
        } : null,
        sol.contractType ? {
            label: 'Contract type',
            value: sol.contractType
        } : null,
        sol.setAside ? {
            label: 'Set-aside',
            value: sol.setAside.replace(/_/g, ' ')
        } : null,
        sol.naicsCode ? {
            label: 'NAICS',
            value: sol.naicsCode,
            mono: true
        } : null
    ].filter((r)=>r !== null);
    if (rows.length === 0) {
        return /*#__PURE__*/ _jsx("span", {
            className: "text-xs text-muted-foreground",
            children: "No solicitation metadata available."
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "divide-y divide-border",
        children: rows.map((row)=>/*#__PURE__*/ _jsxs("div", {
                className: "flex items-baseline justify-between py-1.5",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-muted-foreground",
                        children: row.label
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: `text-sm font-medium ${row.mono ? 'font-mono' : ''}`,
                        children: row.value
                    })
                ]
            }, row.label))
    });
}
/** Fix 7 — Compliance stat breakdown rows below the progress bar. */ function ComplianceBreakdown({ stats }) {
    const rows = [
        {
            label: 'Addressed',
            count: stats.addressed,
            tone: 'bg-success'
        },
        {
            label: 'Partial',
            count: stats.partial,
            tone: 'bg-warning'
        },
        {
            label: 'Not addressed',
            count: stats.notAddressed,
            tone: 'bg-danger'
        },
        {
            label: 'Not applicable',
            count: stats.notApplicable,
            tone: 'bg-muted-foreground'
        }
    ].filter((r)=>r.count > 0);
    if (rows.length === 0) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "mt-3 space-y-0",
        children: rows.map((row)=>/*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 border-t border-border py-1.5 text-xs",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: `size-2 flex-shrink-0 rounded-full ${row.tone}`
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground",
                        children: row.label
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "ml-auto font-mono text-foreground",
                        children: row.count
                    })
                ]
            }, row.label))
    });
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/** Map deadline type to a Tailwind dot color class. */ function deadlineTypeTone(type) {
    if (!type) return 'bg-muted-foreground';
    const t = type.toLowerCase();
    if (t.includes('question')) return 'bg-warning';
    if (t.includes('proposal')) return 'bg-danger';
    return 'bg-muted-foreground';
}
/** Map activity category to a Tailwind dot color class. */ function activityCategoryTone(category) {
    switch(category){
        case 'extraction':
        case 'ai_output':
            return 'bg-brand';
        case 'review':
            return 'bg-warning';
        case 'reversion':
            return 'bg-danger';
        default:
            return 'bg-muted-foreground';
    }
}
function humanizeStatus(s) {
    switch(s){
        case ComplianceStatus.NOT_STARTED:
            return 'Not started';
        case ComplianceStatus.IN_PROGRESS:
            return 'In progress';
        case ComplianceStatus.ADDRESSED:
            return 'Addressed';
        case ComplianceStatus.PARTIAL:
            return 'Partial';
        case ComplianceStatus.NON_COMPLIANT:
            return 'Non-compliant';
        case ComplianceStatus.NOT_APPLICABLE:
            return 'N/A';
        default:
            return s;
    }
}
